import { useEffect, useRef } from 'react';
import {
  RecordedVideo,
  useScreenRecorder,
} from 'contexts/ScreenRecorderContext';
import { videoStorage } from '../../../../utils/videoStorage';

interface UseAutoUploadRecordedVideoParams {
  recordedVideo: RecordedVideo | null;
  sessionId: number;
  onUpload: (fileList: FileList) => Promise<void> | void;
  onOpenForm: () => void;
  shouldOpenForm?: boolean;
}

/**
 * Hook to automatically upload recorded video when recording stops
 * and open the form content
 */
export const useAutoUploadRecordedVideo = ({
  recordedVideo,
  sessionId,
  onUpload,
  onOpenForm,
  shouldOpenForm = true,
}: UseAutoUploadRecordedVideoParams) => {
  const { setRecordedVideo } = useScreenRecorder();
  // Track if THIS SPECIFIC COMPONENT has handled the recording
  // Use timestamp instead of Blob reference because Blob reference can change (e.g. IDB load)
  // while timestamp is stable for the same recording
  const componentLastHandledTimestampRef = useRef<number | null>(null);
  // Track if upload has been triggered to prevent duplicate uploads
  const uploadTriggeredRef = useRef<boolean>(false);

  useEffect(() => {
    // Persistent guard to prevent auto re-open/re-upload when navigating back
    const uploadedKey =
      recordedVideo?.id && `antoree_auto_uploaded_${recordedVideo.id}`;
    if (uploadedKey && sessionStorage.getItem(uploadedKey) === '1') {
      if (recordedVideo) {
        componentLastHandledTimestampRef.current = recordedVideo.timestamp;
      }
      return;
    }

    // If video is already uploaded successfully, mark as handled and skip
    if (recordedVideo?.uploadStatus === 'success') {
      componentLastHandledTimestampRef.current = recordedVideo.timestamp;
      if (uploadedKey) {
        sessionStorage.setItem(uploadedKey, '1');
      }
      return;
    }

    if (
      recordedVideo &&
      recordedVideo.sessionId === String(sessionId) &&
      recordedVideo.blob &&
      componentLastHandledTimestampRef.current !== recordedVideo.timestamp &&
      !uploadTriggeredRef.current &&
      recordedVideo.uploadStatus !== 'uploading' // Don't retry if currently uploading
    ) {
      // Mark this timestamp as handled to prevent duplicate uploads
      componentLastHandledTimestampRef.current = recordedVideo.timestamp;
      uploadTriggeredRef.current = true;

      // Convert blob to File for upload
      const videoFile = new File(
        [recordedVideo.blob],
        `recording-${recordedVideo.sessionId}-${recordedVideo.timestamp}.webm`,
        { type: 'video/webm' },
      );

      // Create a FileList-like object
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(videoFile);
      const fileList = dataTransfer.files;

      // Don't clear recordedVideo - let download modal stay open
      // User can download video anytime, modal won't auto-close

      // Automatically open the form content immediately
      if (shouldOpenForm) {
        onOpenForm();
      }

      // Trigger upload automatically (after a small delay to ensure form is open)
      // Only upload once - use ref to prevent duplicate calls
      const uploadTimeout = setTimeout(async () => {
        // Double check to prevent duplicate uploads
        if (
          componentLastHandledTimestampRef.current ===
            recordedVideo.timestamp &&
          uploadTriggeredRef.current
        ) {
          // Mark as uploaded immediately to prevent duplicate calls
          uploadTriggeredRef.current = false;

          try {
            // Persist a flag right before we start uploading (after opening modal)
            if (uploadedKey) {
              sessionStorage.setItem(uploadedKey, '1');
            }

            // Update status to uploading
            if (recordedVideo.id) {
              await videoStorage.updateVideoStatus(
                recordedVideo.id,
                'uploading',
              );
            }

            // Reflect status in context for downstream guards
            setRecordedVideo({
              ...recordedVideo,
              uploadStatus: 'uploading',
            });

            await onUpload(fileList);

            // Update status to success
            if (recordedVideo.id) {
              await videoStorage.updateVideoStatus(recordedVideo.id, 'success');
            }

            // Reflect success in context to avoid future auto triggers
            setRecordedVideo({
              ...recordedVideo,
              uploadStatus: 'success',
            });
          } catch (err) {
            console.error('Auto upload failed:', err);
            // Update status to failed
            if (recordedVideo.id) {
              await videoStorage.updateVideoStatus(
                recordedVideo.id,
                'failed',
                err instanceof Error ? err.message : 'Unknown error',
              );
            }

            // Reflect failure in context; keep sessionStorage flag to prevent auto re-upload on back
            setRecordedVideo({
              ...recordedVideo,
              uploadStatus: 'failed',
            });
          }
        }
      }, 100);

      // Cleanup
      return () => {
        clearTimeout(uploadTimeout);
      };
    }

    // Reset upload flag when recordedVideo changes to a new recording
    if (!recordedVideo) {
      uploadTriggeredRef.current = false;
      componentLastHandledTimestampRef.current = null;
    }
  }, [recordedVideo, sessionId, onUpload, onOpenForm, setRecordedVideo]);
};
